import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { SESSION_GONE_EVENT } from './sessionGuard';
import AppSidebar from './components/AppSidebar';
import Login from './pages/login';
import RegisterUser from './pages/RegisterUser';
import PlatformSelector from './pages/PlatformSelector';
import Settings from './pages/Settings';
import TeamAccess from './pages/TeamAccess';
import RiskDashboard from './pages/risk/RiskDashboard';
import Analytics from './pages/Analytics';
import ResetPassword from './pages/ResetPassword';
import MfaEnroll from './pages/MfaEnroll';
import MfaChallenge from './pages/MfaChallenge';
import CalendarPage from './pages/Calendar';
import PrivacyProgram from './pages/PrivacyProgram';
import COADashboard from './pages/coa/COADashboard';
import AuditApp from './pages/Audit/AuditApp';
import ComplianceIssuesLog from './pages/complianceIssuesLog/ComplianceIssuesLog';
import NetworkAdequacy from './pages/networkAdequacy/NetworkAdequacy';
import OracleHome from './pages/Oracle/OracleHome';
import WorkstreamHome from './pages/Workstream/WorkstreamHome';
import RespondPage from './pages/Workstream/RespondPage';
import AuditResponsePage from './pages/Audit/AuditResponsePage';
import FdrLinkPage from './pages/Readiness/FdrLinkPage';
import RadvProviderPortal from './pages/Audit/RadvProviderPortal';
import ProviderAttestPage from './pages/networkAdequacy/ProviderAttestPage';
import Workspace from './pages/Workspace';
import PPMCommandCenter from './pages/PPM';
import MandateNative from './pages/MandateNative';
import LaneChat from './pages/Lane/LaneChat';
import PolicyReadingRoom from './pages/PPM/PolicyReadingRoom';
import Reporter from './pages/Reporter';
import UniverseRequests from './pages/UniverseRequests';
import UniversePortal from './pages/UniversePortal';
import ScrubberHub from './pages/ScrubberHub';
import ScrubberPortal from './pages/Scrubber/ScrubberPortal';
import FdrVendorPortal from './pages/FdrVendorPortal';
import AuditeePortal from './pages/AuditeePortal';
import ApprovalsApp from './pages/Approvals';
import FDRCommandCenter from './pages/FDR';
import SentinelCommandCenter from './pages/Sentinel';
import AICommandCenter from './pages/AICommand';
import MaterialsReviewApp from './pages/MaterialsReview';
import ReadinessApp from './pages/Readiness';
import AgentApp from './pages/Agent';
import DeckStudioApp from './pages/DeckStudio/DeckStudioApp';
import PRiQDashboard from './pages/PRiQ';
import CAPCommandCenter from './pages/CAP';
import PortalApp from './portal';
import PlatformAdmin from './pages/PlatformAdmin';
import MetricsDashboard from './pages/Admin/MetricsDashboard';
import SharePointIntakePage from './pages/Admin/SharePointIntakePage';
import AcceptInvite from './pages/AcceptInvite';
import OnboardingWizard from './pages/Onboarding';
import People from './pages/People';
import Workspaces from './pages/Workspaces';
import WorkspaceSwitcher from './components/WorkspaceSwitcher';
import { ensureWorkspace } from './lib/workspaceBoot';
import ImpersonationBanner from './components/ImpersonationBanner';
import NotificationToastHost from './components/NotificationToastHost';
import ReviewerPortal from './pages/PPM/Approvals/ReviewerPortal/ReviewerPortal';
import BoardPortal from './pages/Approvals/BoardPortal/BoardPortal';

function ReviewerPortalRoute() {
  const { token } = useParams();
  return <ReviewerPortal token={token} />;
}

function BoardPortalRoute() {
  const { token } = useParams();
  return <BoardPortal token={token} />;
}

// Cross-subdomain apps live on their own hostnames. Clicking them must navigate
// the whole tab — iframe embedding fails because modern browsers refuse to send
// the shared session cookie to a third-party context, breaking SSO.
const EXTERNAL_APP_HOSTS = {
  memo: { az: 'az-mandate', prod: 'mandate' },
  knowledge: { az: 'az-knowledge', prod: 'knowledge' },
  // scrubber: now a page inside Core (src/pages/Scrubber), no longer a separate host.
};

function getExternalAppUrl(appId) {
  const host = EXTERNAL_APP_HOSTS[appId];
  if (!host) return null;
  const isAzure = typeof window !== 'undefined' && window.location.hostname.startsWith('az-');
  const sub = isAzure ? host.az : host.prod;
  return `https://${sub}.precisioncompliancegroup.com`;
}

// A calm, dismissible top prompt shown to an org admin whose organization has
// not finished first-time setup. It never blocks the app (setup is no longer
// forced); it just offers one click into the onboarding wizard. Renders null
// for everyone else, so it is safe to leave mounted in the app shell.
function SetupBanner({ user }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const ut = user?.user_type || user?.effective_user_type || null;
  const isOrgAdmin =
    ut === 'org_superadmin' ||
    user?.role === 'superuser' ||
    (!ut && user?.role === 'admin' && !user?.department_id);
  if (!isOrgAdmin || user?.organization_setup_complete !== false || dismissed) return null;
  return (
    <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-2.5 bg-gradient-to-r from-amber-500/15 to-orange-500/15 border-b border-amber-500/30">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-amber-300 text-sm">✨</span>
        <span className="text-sm text-amber-100 truncate">
          Finish setting up your organization — confirm your departments, aliases, and first team members.
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate('/onboarding')}
          className="px-4 py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-xs font-semibold text-white hover:shadow-lg transition"
        >
          Finish setup →
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-300/70 hover:text-amber-200 text-lg leading-none px-1"
          title="Dismiss (you can always reopen setup from the sidebar)"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function Home({ user, selectedApp, onSelectApp }) {
  const handleAppSelection = (appId) => {
    const ext = getExternalAppUrl(appId);
    if (ext) {
      // Robust navigation: assign + href fallback. assign() can be noop'd by
      // certain browser extensions / DevTools states; href= is the canonical
      // fallback that always triggers a top-level navigation.
      try { window.location.assign(ext); } catch { /* fall through */ }
      window.location.href = ext;
      return;
    }
    onSelectApp(appId);
  };

  // Deep-link case: user landed on Core with ?app=memo (or knowledge/scrubber).
  // Navigate directly — no splash, no extra click.
  React.useEffect(() => {
    const ext = getExternalAppUrl(selectedApp);
    if (ext) window.location.replace(ext);
  }, [selectedApp, user]);

  const isComplianceOnly = (u) => u?.can_see_privacy === true;
  // The compliance team works and approves policies; officer-only apps
  // (FDR, Privacy, Network Adequacy) keep isComplianceOnly.
  const isComplianceTeam = (u) => u?.can_see_privacy === true || u?.is_compliance_team === true;

  // Server is the authoritative gate for PSI (overmind) — frontend mirrors
  // it here. Dept users who deep-link to /app?app=overmind are quietly
  // redirected to the Workspace home. Done in useEffect (not during render)
  // so React doesn't warn about scheduling an update from inside another
  // component's render phase.
  const canSeePsi = (u) => {
    if (!u) return false;
    const ut = u.user_type || u.effective_user_type || null;
    if (ut === 'org_superadmin') return false;
    if (ut === 'platform_superuser') return u.support_grant_active === true;
    if (ut && ['compliance_officer', 'compliance_analyst'].includes(ut)) return true;
    if (!ut && u.role === 'superuser') return false;
    if (!ut && u.role === 'superadmin') return u.support_grant_active === true;
    if (!ut && u.role === 'admin' && !u.department_id) return true;
    return false;
  };

  useEffect(() => {
    if (selectedApp === 'privacy' && user && !isComplianceOnly(user)) {
      onSelectApp('calendar');
    }
    if (selectedApp === 'privacy_program' && user && !isComplianceOnly(user)) {
      onSelectApp('calendar');
    }
    if (selectedApp === 'network_adequacy' && user && !isComplianceOnly(user)) {
      onSelectApp('calendar');
    }
    if (selectedApp === 'overmind' && user && !canSeePsi(user)) {
      onSelectApp('workspace');
    }
  }, [selectedApp, user, onSelectApp]);

  // PSI (Precision Secured Intelligence — formerly POM) — compliance command
  // center. Frontend gate: dept users who deep-link here render nothing
  // until the useEffect above flips selectedApp to 'workspace'. Server is
  // the authoritative gate at /api/overmind/* via
  // PERMISSIONS.modules.overmind = compliance_only.
  if (selectedApp === 'overmind') {
    if (!canSeePsi(user)) return null;
    return (
      <OracleHome user={user} onSelectApp={handleAppSelection} />
    );
  }

  // Workspace — dept users' home. Clean, focused, shows only what they own.
  // Compliance users have no need for this route since their home is PSI,
  // but they can still reach it via deep link and we render it for them too
  // so the URL never 404s.
  if (selectedApp === 'workspace') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <Workspace user={user} onSelectApp={handleAppSelection} />
        </div>
      </div>
    );
  }

  // PPM — Policy Management with core sidebar. Compliance-level users get
  // the full workbench; department-bound users get the read-only Reading
  // Room of published policies (server enforces the same split in
  // routes/ppm.js — this branch just mirrors it).
  if (selectedApp === 'ppm') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          {isComplianceTeam(user) ? <PPMCommandCenter /> : <PolicyReadingRoom />}
        </div>
      </div>
    );
  }

  // The Regulation desk: a conversation opened from the launcher over the
  // apps the person ticked (Mandate, RuleIQ, Policy). Reads only through
  // /api/lane as this person; never a way around an app's own access rules.
  if (selectedApp === 'lane') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <LaneChat user={user} />
        </div>
      </div>
    );
  }

  // Precision Mandate rebuilt as a native module (parallel-build phase:
  // compliance-only visibility; the standalone Mandate iframe app stays the
  // production surface until parity cutover).
  if (selectedApp === 'mandate_native') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <MandateNative user={user} />
        </div>
      </div>
    );
  }

  // Reporter — Part C/D Reporting Requirements two-file validator
  if (selectedApp === 'reporter') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <Reporter />
        </div>
      </div>
    );
  }

  // Universe Requests — unified FDR portal serving Scrubber + Reporter
  if (selectedApp === 'universe_requests') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <UniverseRequests />
        </div>
      </div>
    );
  }

  // Precision Ratify — one place for every committee/CEO/board sign-off.
  // Deep links from COA, PPM, etc. arrive via ?app=ratify&seedType=...&seedId=...
  // The legacy ?app=approvals key is honored as a backward-compat alias.
  if (selectedApp === 'ratify' || selectedApp === 'approvals') {
    const params = (() => { try { return new URLSearchParams(window.location.search); } catch { return null; } })();
    const seedType = params?.get('seedType') || null;
    const seedId   = params?.get('seedId')   || null;
    const initialView = (seedType && seedId) ? 'wizard' : 'list';
    const initialArtifact = (seedType && seedId) ? { artifact_type: seedType, artifact_id: seedId } : null;
    // Strip the seed params after consumption so a sidebar nav-away/back
    // doesn't re-seed the wizard. window.history.replaceState avoids a remount.
    const consume = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('seedType');
        url.searchParams.delete('seedId');
        window.history.replaceState({}, '', url.toString());
      } catch {}
    };
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <ApprovalsApp
            initialView={initialView}
            initialArtifact={initialArtifact}
            onConsumeInitial={consume}
          />
        </div>
      </div>
    );
  }

  // FDR Oversight — compliance only
  if (selectedApp === 'fdr') {
    if (!isComplianceOnly(user)) return null;
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <FDRCommandCenter />
        </div>
      </div>
    );
  }

  // Sentinel — SIU / FWA investigations. Visible to everyone; the command
  // center itself shows the full workbench to compliance officer/analyst and the
  // narrow "records requested from your team" surface to department users.
  if (selectedApp === 'sentinel') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <SentinelCommandCenter user={user} />
        </div>
      </div>
    );
  }

  // AI Command — responsible AI governance. Visible to everyone: compliance
  // gets the workbench, department users and champions get the propose and
  // issue-logging surface (split enforced server-side and in the shell).
  if (selectedApp === 'ai_command') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <AICommandCenter user={user} />
        </div>
      </div>
    );
  }

  if (selectedApp === 'materials_review') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <MaterialsReviewApp user={user} />
        </div>
      </div>
    );
  }

  if (selectedApp === 'readiness') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <ReadinessApp user={user} />
        </div>
      </div>
    );
  }

  // The Compliance Agent (operator order 2026-10-23): phase A is the
  // compliance Setup page; the server refuses anyone else.
  if (selectedApp === 'agent') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <AgentApp user={user} />
        </div>
      </div>
    );
  }

  // Render Deck Studio (universal deck creator) when selected
  if (selectedApp === 'deck_studio') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <DeckStudioApp user={user} />
        </div>
      </div>
    );
  }

  // Render Risk internal suite when selected
  if (selectedApp === 'risk') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <RiskDashboard user={user} />
        </div>
      </div>
    );
  }

  // Render PRiQ (Precision RuleIQ) when selected
  if (selectedApp === 'priq') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden relative">
          <PRiQDashboard user={user} />
        </div>
      </div>
    );
  }

  // Render Analytics internal suite when selected
  if (selectedApp === 'analytics') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <Analytics user={user} />
        </div>
      </div>
    );
  }

  // Render Calendar internal suite when selected
  if (selectedApp === 'calendar') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <CalendarPage />
        </div>
      </div>
    );
  }

  // Workstream — cross-module follow-up app. Compliance-level only.
  if (selectedApp === 'workstream') {
    if (!canSeePsi(user)) return null;
    return <WorkstreamHome user={user} onSelectApp={handleAppSelection} />;
  }

  // Precision Privacy (2026-08-31): ONE app. The privacy program is the
  // shell; the incident lifecycle (Privacy Command Center, untouched) is a
  // module inside it. 'privacy_program' stays as an alias. Compliance only.
  if (selectedApp === 'privacy' || selectedApp === 'privacy_program') {
    if (!isComplianceOnly(user)) {
      return null;
    }
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={'privacy'} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          <PrivacyProgram user={user} onSelectApp={handleAppSelection} />
        </div>
      </div>
    );
  }

  // Scrubber: one app, the user picks the CMS audit year (audits through 2026
  // on the current package, or the 2027 audit year and later on CMS-10717)
  // and the validation logic follows. ?app=scrubber_2029 is kept as a deep
  // link that opens the hub on the 2027 audit year package.
  if (selectedApp === 'scrubber' || selectedApp === 'scrubber_2029') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp="scrubber" onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <ScrubberHub user={user} initialYear={selectedApp === 'scrubber_2029' ? '2027' : undefined} />
        </div>
      </div>
    );
  }

  // Render Audit & Monitoring Work Plan (COA) when selected
  if (selectedApp === 'coa') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <COADashboard user={user} />
        </div>
      </div>
    );
  }

  // Render the centralized Audit tool (internal + FDR) when selected
  if (selectedApp === 'audit') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <AuditApp scope="all" user={user} />
        </div>
      </div>
    );
  }

  // Render Compliance Issues Log when selected
  if (selectedApp === 'cil') {
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <ComplianceIssuesLog user={user} />
        </div>
      </div>
    );
  }

  if (selectedApp === 'network_adequacy') {
    if (!isComplianceOnly(user)) return null;
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <NetworkAdequacy user={user} />
        </div>
      </div>
    );
  }

  // CAP — Precision Corrective Action Plan, native in-Core module.
  // Replaces the legacy iframe to cap.precisioncompliancegroup.com.
  // Visibility is gated solely on organization_products.cap (the org must own
  // the product). Per-user authorization (who can create / who sees which
  // CAPs) is enforced server-side: GET /api/pcap/caps scopes results per
  // user, and POST /api/pcap/caps requires compliance OR an explicit
  // pcap_creator grant.
  if (selectedApp === 'cap') {
    const orgHasCap = !!(user?.organization_products?.cap);
    if (!orgHasCap) return null;
    return (
      <div className="flex flex-1 min-h-0 min-w-0">
        <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <CAPCommandCenter user={user} />
        </div>
      </div>
    );
  }

  // External apps redirect via the useEffect above; render nothing while the
  // navigation is in flight so the user never sees a splash or a click prompt.
  if (getExternalAppUrl(selectedApp)) return null;

  return (
    <div className="flex flex-1 min-h-0 min-w-0">
      <AppSidebar selectedApp={selectedApp} onSelectApp={handleAppSelection} user={user} />
      <div className="flex-1 min-w-0 min-h-0 overflow-hidden" />
    </div>
  );
}

function ProtectedApp() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  // Org logo for the top bar — served from the Deck Studio branding store
  // (org_branding), the same logo compliance uploaded for their decks. We
  // probe /branding (cheap, org-scoped) and only set the img URL when a
  // logo actually exists, so orgs without one render exactly as before.
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  useEffect(() => {
    if (!user) { setOrgLogoUrl(null); return; }
    if (user.organization_logo_url) { setOrgLogoUrl(user.organization_logo_url); return; }
    let dead = false;
    fetch(`${process.env.REACT_APP_API_URL}/api/deck-studio/branding`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!dead && d?.has_logo) setOrgLogoUrl(`${process.env.REACT_APP_API_URL}/api/deck-studio/branding/logo`); })
      .catch(() => {});
    return () => { dead = true; };
  }, [user]);
  // Deep-link support: COA, PSI, and other modules can land directly on the
  // CAP module by sending users to /app?app=cap&cap_id=<uuid>. The cap_id is
  // read by CAPCommandCenter via sessionStorage so we don't need to thread it
  // through every prop.
  //
  // Default landing is the user's HOME — PSI for compliance, Workspace for
  // department users. We start with 'workspace' as the safe default (least
  // privilege — least data shown) and the user-loaded effect below switches
  // compliance users to 'overmind' once we know who they are. If a
  // ?app=<key> deep link is present that takes precedence.
  //
  // Annual policy review: the button in the review email is
  // /app?app=workspace&review=<campaign>. The campaign id is stored the same
  // way as cap_id BEFORE the session check, so a signed-out reader who is
  // sent to /login lands back on their review afterwards (login → / → /app
  // reads the stored id). Only that button sets it; every other entry lands
  // on the landing page. Workspace consumes and clears it.
  const initialApp = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const requested = params.get('app');
      const capId = params.get('cap_id');
      if (capId) sessionStorage.setItem('pcg_open_cap_id', capId);
      const review = params.get('review');
      if (review && /^[0-9a-f-]{36}$/i.test(review)) sessionStorage.setItem('pcg_open_review', review);
      if (requested) return requested;
      if (sessionStorage.getItem('pcg_open_review')) return 'workspace';
    } catch {}
    return 'workspace';
  })();
  const [selectedApp, setSelectedApp] = useState(initialApp);
  // Tracks whether the initial app came from an explicit ?app= deep link.
  // We use this to decide whether the post-login effect is allowed to
  // override the landing — a user who deep-linked to a specific app should
  // stay on that app, not be bounced to their home. A pending review landing
  // counts as a deep link for the same reason.
  const [initialAppFromDeepLink] = useState(() => {
    try {
      return !!new URLSearchParams(window.location.search).get('app') || !!sessionStorage.getItem('pcg_open_review');
    } catch { return false; }
  });
  const navigate = useNavigate();

  useEffect(() => {
fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
  method: 'GET',
  credentials: 'include'
})

      .then(res => res.json())
      .then(data => {
  if (data.success) {
    // SOC 2 CC6.1 — non-test users without MFA enabled MUST complete enrollment
    // before accessing any other module. The session is active but the
    // enforceMfaEnrollment middleware on the server blocks every other API call.
    if (data.user.mfa_enrollment_required) {
      navigate('/mfa-enroll');
      return;
    }

    // People who work in more than one workspace (TPA staff) choose one
    // first; the choice is remembered on this browser. Everyone else: no-op.
    if (ensureWorkspace(data.user, navigate)) return;

    const { role } = data.user;
    const knownRoles = ['superadmin', 'superuser', 'admin', 'associate'];

    if (!knownRoles.includes(role)) {
      // Truly unregistered — no recognized role
      navigate('/register-user');
      return;
    }

    // Org admins land in the working app just like a compliance officer, even
    // before first-time setup is finished. Onboarding is no longer forced: it is
    // reachable any time from the sidebar "Organization setup" link, and while
    // organization_setup_complete is still false the SetupBanner offers a
    // one-click "Finish setup" prompt. (Removed the former forced redirect to
    // /onboarding, which trapped the super user outside the app.)

    // Stage 8d: superadmin (Platform Superuser) lands on the new Platform
    // Admin Console where they can provision orgs and click "Manage Users →"
    // on any org row. The legacy /settings page is still reachable at the
    // gear icon in the sidebar for legacy Contracts/PBPs/Mapping/CSV flows.
    //
    // Stage 8e: While an impersonation session is active, the platform
    // superuser should stay inside /app so they see the impersonated org's
    // dashboard. The persistent ImpersonationBanner makes the elevated state
    // visually unmistakable, and write affordances are disabled via
    // useImpersonation().
    const isPlatformSuperuser =
      role === 'superadmin' ||
      data.user.user_type === 'platform_superuser' ||
      data.user.effective_user_type === 'platform_superuser';
    // /api/auth/session returns impersonation as a TOP-LEVEL field, not nested under user.
    const isImpersonating = !!data.impersonation && data.impersonation.active !== false;
    if (isPlatformSuperuser && !isImpersonating) {
      navigate('/platform-admin');
      return;
    }

    // 2026-08-24 role redesign: the org super user is the IT access
    // administrator — their home is People (user and access management),
    // not the compliance workspace, which they can no longer see.
    const landingUt = data.user.user_type || data.user.effective_user_type || null;
    const isAccessAdmin = landingUt ? landingUt === 'org_superadmin' : data.user.role === 'superuser';
    if (isAccessAdmin) {
      localStorage.setItem('sessionUser', JSON.stringify(data.user));
      navigate('/people');
      return;
    }

    // 💾 Save session user for Settings.js
    localStorage.setItem('sessionUser', JSON.stringify(data.user));
    setUser(data.user);

    // PSI / Workspace landing: once we know the user, switch compliance users
    // from the safe "workspace" default to PSI. Honor explicit ?app= deep
    // links — a user who linked to /app?app=cap should stay on CAP, not be
    // bounced. Department users stay on workspace (the safe default).
    if (!initialAppFromDeepLink) {
      const ut = data.user.user_type || data.user.effective_user_type || null;
      const isComplianceLevel = ut && ['compliance_officer', 'compliance_analyst'].includes(ut);
      const isLegacyAdmin     = !ut && ['superadmin', 'admin'].includes(data.user.role) && !data.user.department_id;
      if (isComplianceLevel || isLegacyAdmin) {
        setSelectedApp('overmind');
      }
    }
  }
else {
          navigate('/login');
        }
        setLoading(false);
      })
      .catch(() => {
        navigate('/login');
      });
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl animate-pulse">
            <span className="text-white font-extrabold text-base tracking-[0.2em]">PCG</span>
          </div>
          <div className="text-white text-lg font-medium">Loading...</div>
          <div className="mt-2 text-slate-400 text-sm">Preparing your workspace</div>
        </div>
      </div>
    );
  }

  return (
  <div className="flex flex-col h-screen overflow-hidden">
    {/* Persistent impersonation banner — pinned above everything else when a
        platform superuser is acting on behalf of a client org. Renders null
        when no session is active so it's safe to leave mounted. */}
    <ImpersonationBanner />
    <SetupBanner user={user} />
    {/* In-app notification toaster — polls /api/notifications/unread-count
        every 30s while the tab is visible. When the count rises (e.g. a
        memo finishes analyzing in Mandate) it shows a calm slide-in toast
        for each new notification, click-through opens the linked app. */}
    <NotificationToastHost />
    {/* Modern SaaS Header: branding (left), organization name (center-left), user/logout (right) */}
    <div className="sticky top-0 z-50 flex-shrink-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-4 border-b border-slate-600 shadow-2xl">
      <div className="flex items-center justify-between gap-4">
        {/* Left: Organization identity. Client logos are almost always
            wordmarks (they contain the client's name), so when a logo
            exists it REPLACES the text name — never both, never
            "Solis [Solis logo]". Orgs without a logo keep the text name. */}
        <div className="flex items-center min-w-0 space-x-4">
          {orgLogoUrl ? (
            <span className="flex-shrink-0 inline-flex items-center bg-white rounded-xl px-3.5 py-2 shadow-md" title={user?.organization_name || ''}>
              <img
                src={orgLogoUrl}
                alt={user?.organization_name || 'Organization logo'}
                onError={() => setOrgLogoUrl(null)}
                className="object-contain object-left"
                style={{ height: '40px', maxWidth: '220px', width: 'auto' }}
              />
            </span>
          ) : (
            <div className="min-w-0">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500 bg-clip-text text-transparent tracking-tight leading-tight truncate">
                {user?.organization_name || 'Precision Compliance Group'}
              </h1>
              <div className="w-20 h-0.5 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full mt-1 shadow-sm"></div>
            </div>
          )}
          <WorkspaceSwitcher user={user} />
        </div>

        {/* Right: User Info and Logout */}
        <div className="flex items-center space-x-4">
          {/* Manage Team — surfaces the People page (add/remove/edit users,
              departments) for anyone whose user_type can administer their own
              org. Matches the server-side requireOrgManageAccess gate so what
              appears here is exactly what the user is authorized to do. */}
          {(() => {
            const utype = user?.user_type || user?.effective_user_type || null;
            const canManage =
              utype === 'platform_superuser' ||
              utype === 'org_superadmin' ||
              utype === 'compliance_officer' ||
              user?.role === 'superadmin' ||
              user?.role === 'superuser' ||
              (!utype && user?.role === 'admin' && !user?.department_id);
            if (!canManage) return null;
            return (
              <button
                onClick={() => navigate('/people')}
                title="Add, edit, deactivate, or replace users in your organization"
                className="px-4 py-2.5 bg-gradient-to-r from-orange-600/70 to-amber-600/70 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl border border-orange-400/40 hover:border-orange-300 transition-all duration-200 font-medium text-sm flex items-center gap-2 shadow-lg shadow-orange-500/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                <span>Manage Team</span>
              </button>
            );
          })()}

          {/* User Info Card */}
          <div className="bg-slate-700/80 border border-slate-600 rounded-xl px-4 py-2.5 shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-sm"></div>
              <div className="text-right">
                <div className="text-white font-semibold text-sm leading-tight">{user?.name}</div>
                <div className="text-slate-300 text-xs leading-tight">Online</div>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center border border-slate-500/30">
                <span className="text-white font-semibold text-xs">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={() => {
              fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
                method: 'GET',
                credentials: 'include'
              }).then(() => {
                window.location.href = '/login';
              });
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-red-600/60 to-red-700/60 hover:from-red-600/80 hover:to-red-700/80 text-white hover:text-red-100 rounded-xl border border-red-500 hover:border-red-400 transition-all duration-300 hover:scale-105 hover:shadow-lg font-medium text-sm"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </div>
          </button>
        </div>
      </div>
    </div>
    {/* PRiQ global analysis progress — visible from any module */}
    <PRiQProgressBar onNavigate={() => setSelectedApp('priq')} />
    <div className="flex-1 min-h-0 overflow-hidden flex">
              <Home user={user} selectedApp={selectedApp} onSelectApp={setSelectedApp} />
    </div>
  </div>
);

}

function PMRAShell() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
      method: 'GET',
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUser(data.user);
        } else {
          navigate('/login');
        }
        setLoading(false);
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl animate-pulse">
            <span className="text-3xl text-white">P</span>
          </div>
          <div className="text-white text-lg font-medium">Loading PMRA...</div>
          <div className="mt-2 text-slate-400 text-sm">Precision Medicare Risk Adjustment</div>
        </div>
      </div>
    );
  }

  // Use Azure FQDN directly until az-pmra custom domain DNS is configured
  const pmraBase = window.location.hostname === 'localhost'
    ? 'http://localhost:5500'
    : 'https://pcg-pmra.wonderfultree-b302ca75.eastus2.azurecontainerapps.io';
  // Pass session user to PMRA via URL hash (cross-domain session sharing)
  const sessionData = user ? encodeURIComponent(JSON.stringify({ id: user.id, name: user.name, email: user.email, role: user.role, organization_id: user.organization_id, organization_name: user.organization_name })) : '';
  const pmraUrl = `${pmraBase}#session=${sessionData}`;

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <ImpersonationBanner />
      {/* PMRA Header */}
      <div className="sticky top-0 z-50 flex-shrink-0 bg-gradient-to-r from-slate-900 via-[#0a1a15] to-slate-900 px-6 py-4 border-b border-emerald-900/30 shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center min-w-0 space-x-4">
            {user?.organization_logo_url && (
              <img src={user.organization_logo_url} alt="" className="object-contain object-left flex-shrink-0" style={{ height: '72px', maxWidth: '400px', width: 'auto' }} />
            )}
            <div className="min-w-0">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-500 bg-clip-text text-transparent tracking-tight leading-tight truncate">
                PMRA
              </h1>
              <div className="text-slate-500 text-xs font-medium tracking-wide">Precision Medicare Risk Adjustment</div>
              <div className="w-20 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full mt-1 shadow-sm"></div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2.5 bg-slate-700/60 hover:bg-slate-700/80 text-slate-300 hover:text-white rounded-xl border border-slate-600 transition-all duration-200 text-sm font-medium"
            >
              Switch Platform
            </button>
            <div className="bg-slate-700/80 border border-slate-600 rounded-xl px-4 py-2.5 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-sm"></div>
                <div className="text-right">
                  <div className="text-white font-semibold text-sm leading-tight">{user?.name}</div>
                  <div className="text-slate-300 text-xs leading-tight">Online</div>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, { method: 'GET', credentials: 'include' })
                  .then(() => { window.location.href = '/login'; });
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-red-600/60 to-red-700/60 hover:from-red-600/80 hover:to-red-700/80 text-white rounded-xl border border-red-500 hover:border-red-400 transition-all duration-300 font-medium text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <iframe
          title="PMRA"
          src={pmraUrl}
          className="w-full h-full border-0"
        />
      </div>
    </div>
  );
}

function PNAShell() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
      method: 'GET',
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUser(data.user);
        } else {
          navigate('/login');
        }
        setLoading(false);
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl animate-pulse">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
            </svg>
          </div>
          <div className="text-white text-lg font-medium">Loading PNA...</div>
          <div className="mt-2 text-slate-400 text-sm">Precision Network Adequacy</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <ImpersonationBanner />
      {/* PNA Header */}
      <div className="sticky top-0 z-50 flex-shrink-0 bg-gradient-to-r from-slate-900 via-[#0a1520] to-slate-900 px-6 py-4 border-b border-cyan-900/30 shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center min-w-0 space-x-4">
            {user?.organization_logo_url && (
              <img src={user.organization_logo_url} alt="" className="object-contain object-left flex-shrink-0" style={{ height: '72px', maxWidth: '400px', width: 'auto' }} />
            )}
            <div className="min-w-0">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 bg-clip-text text-transparent tracking-tight leading-tight truncate">
                PNA
              </h1>
              <div className="text-slate-500 text-xs font-medium tracking-wide">Precision Network Adequacy</div>
              <div className="w-20 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mt-1 shadow-sm"></div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2.5 bg-slate-700/60 hover:bg-slate-700/80 text-slate-300 hover:text-white rounded-xl border border-slate-600 transition-all duration-200 text-sm font-medium"
            >
              Switch Platform
            </button>
            <div className="bg-slate-700/80 border border-slate-600 rounded-xl px-4 py-2.5 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-sm"></div>
                <div className="text-right">
                  <div className="text-white font-semibold text-sm leading-tight">{user?.name}</div>
                  <div className="text-slate-300 text-xs leading-tight">Online</div>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, { method: 'GET', credentials: 'include' })
                  .then(() => { window.location.href = '/login'; });
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-red-600/60 to-red-700/60 hover:from-red-600/80 hover:to-red-700/80 text-white rounded-xl border border-red-500 hover:border-red-400 transition-all duration-300 font-medium text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden flex">
        <div className="flex-1 min-w-0 min-h-0 overflow-auto">
          <NetworkAdequacy user={user} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PRiQ Global Progress Bar + Completion Toaster
// Visible from any module. Detects running→completed/failed transitions and
// surfaces them as in-app toasts and (with permission) browser notifications.
// ---------------------------------------------------------------------------
// What RuleIQ is doing, in plain words (mirrors src/pages/PRiQ).
const PRIQ_STEP = { pass_0_finalization: 'Reading the regulation text', pass_amendatory: 'Reading the regulation text', pass_preamble_only: 'Reading CMS’s decisions', pass_6_comment_intelligence: 'Reading the public comments', pass_3_gaps: 'Organizing by department', pass_4_intelligence: 'Organizing by department', title_polish: 'Organizing by department', department_mapping: 'Organizing by department', agent_integration: 'Sharing with the policy app', rule_summary: 'Writing the summary' };
function priqStepLabel(a) {
  if (!a) return '';
  if (a.status === 'uploading' || a.status === 'extracting') return 'Opening the rule';
  if (a.status === 'generating') return 'Preparing the reports';
  return PRIQ_STEP[a.current_pass] || 'Reading the rule';
}
// When the server answers that there is no session (see src/sessionGuard.js),
// the app returns to the sign-in screen. Leaving the signed-in shell unmounts
// every poller with it, so a signed-out tab stops calling the server and the
// platform can idle. Pages that work without a session are left alone.
const PUBLIC_PATHS = ['/login', '/mfa-challenge', '/mfa-enroll', '/reset-password', '/register-user', '/accept-invite', '/workstream/respond', '/audit/respond', '/readiness/respond', '/provider/attest', '/radv-portal'];
export function isPublicPath(p) { return PUBLIC_PATHS.some((x) => p === x || p.startsWith(x + '/')); }
function SessionWatch() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = React.useRef(location.pathname);
  React.useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);
  React.useEffect(() => {
    const onGone = () => {
      if (isPublicPath(pathRef.current || '')) return;
      try { localStorage.removeItem('sessionUser'); } catch { /* ignore */ }
      navigate('/login', { replace: true });
    };
    window.addEventListener(SESSION_GONE_EVENT, onGone);
    return () => window.removeEventListener(SESSION_GONE_EVENT, onGone);
  }, [navigate]);
  return null;
}

function PRiQProgressBar({ onNavigate }) {
  const [running, setRunning] = React.useState(null);
  const [toasts, setToasts] = React.useState([]);
  const prevRunningRef = React.useRef(null); // Set<id> of previously-running analyses; null on first poll
  const onNavigateRef = React.useRef(onNavigate);
  const API = process.env.REACT_APP_API_URL || '';

  React.useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  React.useEffect(() => {
    let active = true;
    const isRunning = a => !['completed', 'failed'].includes(a.status);

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/priq/analyses`, { credentials: 'include' });
        const data = await res.json();
        if (!active || !data.success || !data.analyses) return;
        const analyses = data.analyses;

        // Detect transitions: any ID that was running last poll and is no longer running now
        if (prevRunningRef.current !== null) {
          const prev = prevRunningRef.current;
          const newToasts = [];
          for (const id of prev) {
            const current = analyses.find(a => a.id === id);
            if (!current || isRunning(current)) continue;
            // Transitioned to completed or failed
            newToasts.push({
              key: `${id}-${Date.now()}`,
              analysisId: id,
              kind: current.status, // 'completed' | 'failed'
              title: current.title,
              findingCount: current.finding_count,
              reportCount: current.report_count,
            });
            // Browser notification (only if user granted permission earlier)
            try {
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const isFail = current.status === 'failed';
                const n = new Notification(
                  isFail ? 'RuleIQ could not finish reading a rule' : 'A rule is ready in RuleIQ',
                  {
                    body: isFail
                      ? `${current.title}\nClick to review.`
                      : `${current.title}\nSummary, decisions and reports are ready.`,
                    tag: `priq-${id}`,
                    icon: '/favicon.ico',
                  }
                );
                n.onclick = () => {
                  window.focus();
                  if (onNavigateRef.current) onNavigateRef.current();
                  n.close();
                };
              }
            } catch { /* silent */ }
          }
          if (newToasts.length > 0) {
            setToasts(prev2 => [...prev2, ...newToasts]);
          }
        }

        prevRunningRef.current = new Set(analyses.filter(isRunning).map(a => a.id));
        setRunning(analyses.find(isRunning) || null);
      } catch { /* silent */ }
    };

    poll();
    const timer = setInterval(poll, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [API]);

  // Auto-dismiss toasts after 60 seconds (each one independently)
  React.useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map(t =>
      setTimeout(() => {
        setToasts(prev => prev.filter(x => x.key !== t.key));
      }, 60000)
    );
    return () => { timers.forEach(clearTimeout); };
  }, [toasts]);

  const dismissToast = (key) => setToasts(prev => prev.filter(t => t.key !== key));

  return (
    <>
      {/* Slim progress bar — shown when at least one analysis is running */}
      {running && (
        <div
          onClick={onNavigate}
          className="flex-shrink-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20 px-4 py-1.5 flex items-center gap-3 cursor-pointer hover:bg-amber-500/15 transition-all"
        >
          <span className="text-amber-400 animate-pulse text-xs">&#9679;</span>
          <span className="text-xs text-amber-300 font-medium">RuleIQ is reading a rule</span>
          <span className="text-xs text-slate-400 truncate max-w-xs">{running.title}</span>
          <span className="text-xs text-slate-400">·</span>
          <span className="text-xs text-slate-400">{priqStepLabel(running)}</span>
          <div className="flex-1 max-w-xs h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-700"
              style={{ width: `${running.progress_pct || 0}%` }}
            />
          </div>
          <span className="text-xs text-slate-400">{running.progress_pct || 0}%</span>
        </div>
      )}

      {/* Completion toast stack — fixed top-right, dismissible, click-to-navigate */}
      {toasts.length > 0 && (
        <div className="fixed top-24 right-6 z-[100] flex flex-col gap-2 max-w-sm pointer-events-none">
          {toasts.map(t => {
            const isFail = t.kind === 'failed';
            return (
              <div
                key={t.key}
                className={`pointer-events-auto relative rounded-xl border shadow-2xl backdrop-blur-sm p-4 pr-10 transition-all ${
                  isFail
                    ? 'bg-red-50 border-red-300'
                    : 'bg-gradient-to-br from-emerald-50 to-amber-50 border-amber-300'
                }`}
              >
                <button
                  onClick={() => dismissToast(t.key)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 text-xl leading-none w-6 h-6 flex items-center justify-center"
                  aria-label="Dismiss"
                >
                  ×
                </button>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${isFail ? 'text-red-500' : 'text-emerald-500'}`}>
                    {isFail ? '⚠' : '✓'}
                  </span>
                  <p className={`text-sm font-semibold ${isFail ? 'text-red-700' : 'text-amber-800'}`}>
                    {isFail ? 'RuleIQ could not finish reading a rule' : 'A rule is ready in RuleIQ'}
                  </p>
                </div>
                <p className="text-xs text-slate-700 mb-2 line-clamp-2">{t.title}</p>
                {!isFail && (t.findingCount > 0 || t.reportCount > 0) && (
                  <p className="text-xs text-slate-500 mb-3">
                    Summary, decisions and reports are ready.
                  </p>
                )}
                <button
                  onClick={() => { dismissToast(t.key); onNavigate(); }}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${
                    isFail
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-md'
                  }`}
                >
                  {isFail ? 'Open' : 'Open the rule'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function App() {
  // Precision CAP Portal — same React bundle, dispatched by hostname.
  // The portal serves on:
  //   - cap.precisioncompliancegroup.com         (re-uses the legacy CAP subdomain
  //                                               once the standalone CAP app is
  //                                               decommissioned and DNS re-pointed)
  //   - az-cap.precisioncompliancegroup.com      (staging twin)
  //   - cap-portal.precisioncompliancegroup.com  (kept as an alternate alias)
  //   - localhost/_portal/*                      (local dev without DNS)
  const host = window.location.hostname;
  const isPortalHost =
    host === 'cap.precisioncompliancegroup.com' ||
    host === 'az-cap.precisioncompliancegroup.com' ||
    host.startsWith('cap-portal.') ||
    (host === 'localhost' && window.location.pathname.startsWith('/_portal'));
  if (isPortalHost) {
    return <PortalApp />;
  }

  return (
    <Router>
<SessionWatch />
<Routes>
  <Route path="/login" element={<Login />} />
  <Route path="/mfa-challenge" element={<MfaChallenge />} />
  <Route path="/mfa-enroll" element={<MfaEnroll />} />
  <Route path="/reset-password" element={<ResetPassword />} />
  <Route path="/register-user" element={<RegisterUser />} />
  <Route path="/" element={<PlatformSelector />} />
  <Route path="/app" element={<ProtectedApp />} />
  <Route path="/calendar" element={<ProtectedApp />} />
  <Route path="/settings" element={<Settings />} />
  <Route path="/team-access" element={<TeamAccess />} />
  <Route path="/pmra" element={<PMRAShell />} />
  <Route path="/pna" element={<PNAShell />} />
  <Route path="/platform-admin" element={<PlatformAdmin />} />
  <Route path="/admin/metrics" element={<MetricsDashboard />} />
  <Route path="/admin/sharepoint-intake" element={<SharePointIntakePage />} />
  <Route path="/workstream/respond/:token" element={<RespondPage />} />
  <Route path="/audit/respond/:token" element={<AuditResponsePage />} />
  {/* Readiness for FDRs: the entity contact's personal link, no PSI login */}
  <Route path="/readiness/respond/:token" element={<FdrLinkPage />} />
  {/* RADV provider upload door: the link in the record request plus an emailed code (2026-09-26) */}
  <Route path="/radv-portal/:token" element={<RadvProviderPortal />} />
  {/* Provider office attestation: the link in the email is the credential (2026-09-10) */}
  <Route path="/provider/attest/:token" element={<ProviderAttestPage />} />
  <Route path="/accept-invite" element={<AcceptInvite />} />
  <Route path="/onboarding" element={<OnboardingWizard />} />
  <Route path="/people" element={<People />} />
  <Route path="/workspaces" element={<Workspaces />} />
  {/* Public reviewer portal — magic-link recipients land here, no app shell */}
  <Route path="/approve/:token" element={<ReviewerPortalRoute />} />
  {/* Public board self-service portal — long-lived per-email token */}
  <Route path="/board/:token" element={<BoardPortalRoute />} />
  {/* Public FDR-facing Universe Portal — magic-link recipients land here, no app shell */}
  <Route path="/universe-portal/:token" element={<UniversePortal />} />
  {/* Public Scrubber vendor portal — invited FDRs land here with a one time link, no app shell */}
  <Route path="/scrubber-portal/:token" element={<ScrubberPortal />} />
  {/* The Scrubber mailer links invitations to /fdr/portal/<token>; same page */}
  <Route path="/fdr/portal/:token" element={<ScrubberPortal />} />
  {/* Public FDR vendor portal — magic-link recipients, no app shell */}
  <Route path="/fdr-portal" element={<FdrVendorPortal />} />
  {/* Public auditee-facing Audit Portal — grant-link recipients land here, no app shell */}
  <Route path="/audit-portal/:token" element={<AuditeePortal />} />
</Routes>

    </Router>
  );
}

export default App;
// Build: 1775395702
